import { useState } from 'react';
import JSZip from 'jszip';
import { useLazyDownloadFileQuery, useLazyDownloadArchiveQuery } from '../features/fileSystem/filesStorage';
import { FileItem } from '../types/interfaces/filesStorage';
import {
  isDownloadArchiveError,
  isDownloadArchivePresigned,
} from '../types/interfaces';

interface DownloadState {
  downloadingItems: Set<string>;
  downloadProgress: Record<string, number | undefined>;
  downloadErrors: Record<string, string | undefined>;
}

interface DownloadResult {
  success: boolean;
  message: string;
  error?: string;
}

export const useDownloadManager = () => {
  const [state, setState] = useState<DownloadState>({
    downloadingItems: new Set(),
    downloadProgress: {},
    downloadErrors: {},
  });

  const [triggerDownloadFile] = useLazyDownloadFileQuery();
  const [triggerDownloadArchive] = useLazyDownloadArchiveQuery();

  const getItemKey = (item: FileItem) => `${item.fsource}-${item.fname}`;

  const isDownloading = (item: FileItem) => state.downloadingItems.has(getItemKey(item));

  const getDownloadProgress = (item: FileItem) => state.downloadProgress[getItemKey(item)] || 0;

  const getDownloadError = (item: FileItem) => state.downloadErrors[getItemKey(item)];

  const setDownloading = (item: FileItem, downloading: boolean) => {
    const itemKey = getItemKey(item);
    setState(prev => {
      const newDownloadingItems = new Set(prev.downloadingItems);
      if (downloading) {
        newDownloadingItems.add(itemKey);
      } else {
        newDownloadingItems.delete(itemKey);
      }

      return {
        ...prev,
        downloadingItems: newDownloadingItems,
        downloadProgress: downloading
          ? { ...prev.downloadProgress, [itemKey]: 0 }
          : { ...prev.downloadProgress, [itemKey]: undefined },
        downloadErrors: downloading
          ? { ...prev.downloadErrors, [itemKey]: undefined }
          : { ...prev.downloadErrors, [itemKey]: undefined },
      };
    });
  };

  const setDownloadProgress = (item: FileItem, progress: number) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadProgress: { ...prev.downloadProgress, [itemKey]: progress },
    }));
  };

  const setDownloadError = (item: FileItem, error: string) => {
    const itemKey = getItemKey(item);
    setState(prev => ({
      ...prev,
      downloadErrors: { ...prev.downloadErrors, [itemKey]: error },
    }));
  };

  const triggerDownload = (item: FileItem, filename: string) => {
    setDownloadProgress(item, 100);
    setTimeout(() => {
      setDownloading(item, false);
    }, 500);
  };

    const downloadFile = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    setDownloadProgress(item, 10);

    let currentProgress = 10;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 10, 90);
      setDownloadProgress(item, currentProgress);
    }, 200);

    try {
      const response = await triggerDownloadFile({
        source: item.fsource,
        fileName: item.fname,
      }).unwrap();

      clearInterval(progressInterval);
      setDownloadProgress(item, 95);

      if (response && response.file_urls && response.file_urls.length > 0) {
        const link = document.createElement("a");
        link.href = response.file_urls[0];
        link.download = item.fname;
        document.body.appendChild(link);
        link.click();
        link.remove();

        triggerDownload(item, item.fname);
        return {
          success: true,
          message: `File "${item.fname}" downloaded successfully`
        };
      } else {
        const error = 'No download URL available';
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      }
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadError(item, errorMessage);
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

    const downloadArchive = async (item: FileItem): Promise<DownloadResult> => {
    if (isDownloading(item)) {
      return { success: false, message: 'Download already in progress' };
    }

    setDownloading(item, true);
    setDownloadProgress(item, 10);

    let currentProgress = 10;
    const progressInterval = setInterval(() => {
      currentProgress = Math.min(currentProgress + 5, 85);
      setDownloadProgress(item, currentProgress);
    }, 200);

    try {
      let response = await triggerDownloadArchive({
        source: item.fsource,
        fileName: item.fname,
      }).unwrap();

      if (isDownloadArchiveError(response)) {
        clearInterval(progressInterval);
        setDownloadError(item, response.error);
        setDownloading(item, false);
        return { success: false, message: response.error };
      }

      if (!isDownloadArchivePresigned(response)) {
        clearInterval(progressInterval);
        const error = 'Unexpected response format for folder download';
        setDownloadError(item, error);
        setDownloading(item, false);
        return { success: false, message: error };
      }

      clearInterval(progressInterval);
      setDownloadProgress(item, 15);

      let allEntries = [...response.file_entries];
      let nextToken: string | null | undefined = response.next_token;
      let pagesFetched = 1;

      while (nextToken) {
        const nextResponse = await triggerDownloadArchive({
          source: item.fsource,
          fileName: item.fname,
          next_token: nextToken,
        }).unwrap();

        if (isDownloadArchiveError(nextResponse)) {
          setDownloadError(item, nextResponse.error);
          setDownloading(item, false);
          return { success: false, message: nextResponse.error };
        }
        if (isDownloadArchivePresigned(nextResponse)) {
          allEntries = allEntries.concat(nextResponse.file_entries);
          nextToken = nextResponse.next_token;
          pagesFetched += 1;
          setDownloadProgress(item, 15 + Math.min(pagesFetched * 3, 9));
        } else {
          break;
        }
      }

      const filename = response.filename || `${item.fname}.zip`;

      if (allEntries.length === 1 && allEntries[0].relative_path.toLowerCase().endsWith('.zip')) {
        setDownloadProgress(item, 95);
        const link = document.createElement('a');
        link.href = allEntries[0].url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        triggerDownload(item, filename);
        return { success: true, message: `"${item.fname}" download started` };
      }

      if (allEntries.length === 0) {
        setDownloadError(item, 'No files in folder');
        setDownloading(item, false);
        return { success: false, message: 'No files in folder' };
      }

      const zip = new JSZip();
      const total = allEntries.length;
      const progressBatch = total <= 100 ? 1 : Math.ceil(total / 50);
      const progressStart = 25;
      const progressRange = 65;

      for (let i = 0; i < allEntries.length; i++) {
        const entry = allEntries[i];
        const res = await fetch(entry.url);
        if (!res.ok) throw new Error(`Failed to fetch ${entry.relative_path}`);
        const blob = await res.blob();
        zip.file(entry.relative_path, blob);
        const done = i + 1;
        if (done % progressBatch === 0 || done === total) {
          setDownloadProgress(item, progressStart + Math.floor((progressRange * done) / total));
        }
      }

      setDownloadProgress(item, 95);

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      triggerDownload(item, filename);
      return {
        success: true,
        message: `Folder "${item.fname}" downloaded successfully`,
      };
    } catch (error) {
      clearInterval(progressInterval);
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadError(item, errorMessage);
      setDownloading(item, false);
      return { success: false, message: errorMessage, error: errorMessage };
    }
  };

  const downloadItem = async (item: FileItem): Promise<DownloadResult> => {
    if (item.ftype === "folder") {
      return downloadArchive(item);
    } else {
      return downloadFile(item);
    }
  };

  return {
    downloadingItems: state.downloadingItems,
    downloadProgress: state.downloadProgress,
    downloadErrors: state.downloadErrors,

    isDownloading,
    getDownloadProgress,
    getDownloadError,
    downloadItem,
    downloadFile,
    downloadArchive,
  };
};
